import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import SEO from '#/shared/components/SEO';
import useNavigate from '#/shared/hooks/useNavigate';
import ROUTES from '#/constants/routes';
import VehicleDetailHeader from '#/transit-tracker/components/VehicleDetailHeader';
import VehicleStopsList from '#/transit-tracker/components/VehicleStopsList';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import VehicleLoading from '#/transit-tracker/components/VehicleLoadingPrompt';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import useHighlightedStop from '#/transit-tracker/hooks/useHighlightedStop';
import useFavoriteStops from '#/transit-tracker/hooks/useFavoriteStops';
import useSingleRealtimeVehicle from '#/map/hooks/useSingleRealtimeVehicle';
import useVehicleCentering from '#/transit-tracker/hooks/useVehicleCentering';
import TimestampRefreshButton, {
  TimestampRefreshWrapper,
} from '#/shared/components/TimestampRefreshButton';
import { BUS_MODE } from '#/shared/constants';
import mapConfig from '#/map-config';

function VehicleDetailContainer({ routeId, blockId, selectedStopId }) {
  const { navigate, generateNavigateBack, generateNavigateClose } =
    useNavigate();
  const navigateToStop = stopId => navigate(ROUTES.stopById, stopId);

  const { isFavoriteStop } = useFavoriteStops();
  const { setHighlightedStop } = useHighlightedStop();
  const isArrivalsError = useSelector(s => s.transitTracker.isArrivalsError);

  const { vehicleDetailViewOpened, viewClosed } = useViewMapControls();
  useEffect(() => {
    vehicleDetailViewOpened();
    return viewClosed;
  }, [vehicleDetailViewOpened, viewClosed]);

  const { realtimeVehicleRefreshInterval: refreshInterval } = mapConfig;
  const {
    vehicle,
    vehicleLoading,
    vehicleNotFound,
    refetchVehicle,
    vehicleRoute,
    vehicleRouteTimestamp,
  } = useSingleRealtimeVehicle(routeId, blockId, {
    pollingInterval: refreshInterval,
  });

  const { vehicleCenteringActive, centerVehicleOnMap } = useVehicleCentering();
  const intl = useIntl();

  return (
    <>
      {vehicle && vehicleRoute && (
        <SEO
          metadataId="vehicleTracking"
          dynamicValues={{
            routeNumber: vehicleRoute.id,
            routeName: `${vehicleRoute.vanityName} to ${vehicle.signMessage}`,
          }}
        />
      )}
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={generateNavigateBack([ROUTES.home])}
            onClose={generateNavigateClose([ROUTES.stopById, ROUTES.trip])}
            content={
              <>
                {vehicleNotFound && !vehicleLoading && (
                  <FormattedMessage id="detailview.vehiclenotfoundheader" />
                )}
                {vehicle && vehicleRoute && (
                  <FormattedMessage
                    id="detailview.vehicletitleid"
                    values={{
                      id: vehicle.vehicleId,
                      type: intl.formatMessage({
                        id: `detailview.${
                          vehicle.routeType === BUS_MODE
                            ? 'bus-vehicle-name'
                            : 'generic-vehicle-name'
                        }`,
                      }),
                    }}
                  />
                )}
              </>
            }
          />
        }
        middle={
          !isArrivalsError &&
          vehicle &&
          vehicleRoute && (
            <VehicleDetailHeader
              vehicle={vehicle}
              route={vehicleRoute}
              vehicleCenteringActive={vehicleCenteringActive}
              centerVehicleOnMap={centerVehicleOnMap}
            />
          )
        }
        bottom={
          isArrivalsError ? (
            <ErrorWithCountdown
              refreshInterval={refreshInterval}
              errorMessageId="arrivals.error"
            />
          ) : (
            <>
              {vehicle && vehicleRoute && (
                <>
                  <TimestampRefreshWrapper>
                    <TimestampRefreshButton
                      timestamp={vehicleRouteTimestamp}
                      onClick={refetchVehicle}
                    />
                  </TimestampRefreshWrapper>
                  <VehicleStopsList
                    stops={vehicleRoute.stops}
                    isFavoriteStop={isFavoriteStop}
                    selectedStopId={selectedStopId}
                    navigateToMap={navigateToStop}
                    highlightStop={setHighlightedStop}
                    nextArrivalStopId={vehicle.nextLocId}
                  />
                </>
              )}
              {vehicleNotFound &&
                (vehicleLoading ? (
                  <VehicleLoading />
                ) : (
                  <ErrorWithCountdown
                    refreshInterval={refreshInterval}
                    errorMessageId="detailview.vehiclenotfound"
                  />
                ))}
            </>
          )
        }
      />
    </>
  );
}

VehicleDetailContainer.propTypes = {
  routeId: PropTypes.string,
  blockId: PropTypes.string,
  selectedStopId: PropTypes.string,
};

VehicleDetailContainer.defaultProps = {
  routeId: null,
  blockId: null,
  selectedStopId: null,
};

export default VehicleDetailContainer;
